
import React, { useCallback, useEffect, useRef, useState } from "react";

import { getFighters } from "../../../../services/api";
import type { TranslationFn } from "../dealFormFields";
import { humanizeLabel } from "../dealFormFields";

interface FighterItem {
  id: number;
  firstName?: string;
  lastName?: string;
  profileImageUrl?: string;
  profileImage?: string;
  imageUrl?: string;
  photo?: string;
  weightClass?: string;
  [key: string]: unknown;
}

const _fightersCache: {
  data: FighterItem[] | null;
  promise: Promise<FighterItem[]> | null;
} = {
  data: null,
  promise: null,
};

export function prefetchFighters(): void {
  if (_fightersCache.data || _fightersCache.promise) return;
  _fightersCache.promise = getFighters({ pageSize: 100 })
    .then((res) => {
      _fightersCache.data = Array.isArray(res.data) ? res.data : [];
      return _fightersCache.data!;
    })
    .catch(() => []);
}

interface FighterSearchFieldProps {
  currentId: number | string;
  currentName: string;
  placeholder: string;
  tr: TranslationFn;
  allowManualEntry?: boolean;
  isInvalid?: boolean;
  onChange: (id: number | string, name: string, isManual?: boolean) => void;
}

const FighterAvatar: React.FC<{ fighter: FighterItem; size?: "sm" | "md" }> = ({
  fighter,
  size = "sm",
}) => {
  const imgUrl =
    fighter.profileImageUrl ??
    fighter.profileImage ??
    fighter.imageUrl ??
    fighter.photo ??
    null;

  const dim = size === "sm" ? "w-7 h-7" : "w-9 h-9";
  const text = size === "sm" ? "text-[11px]" : "text-[13px]";

  const initials =
    [fighter.firstName, fighter.lastName]
      .filter(Boolean)
      .map((n) => String(n)[0].toUpperCase())
      .join("")
      .slice(0, 2) || "?";

  if (imgUrl) {
    return (
      <img
        src={imgUrl}
        alt={initials}
        className={`${dim} rounded-full object-cover flex-shrink-0 border border-slate-200`}
        onError={(e) => {
          (e.target as HTMLImageElement).style.display = "none";
        }}
      />
    );
  }

  return (
    <div
      className={`${dim} rounded-full bg-blue-100 text-blue-700 flex items-center justify-center flex-shrink-0 font-semibold ${text}`}
    >
      {initials}
    </div>
  );
};

export const FighterSearchField: React.FC<FighterSearchFieldProps> = ({
  currentId,
  currentName,
  placeholder,
  tr,
  allowManualEntry = false,
  isInvalid,
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [fighters, setFighters] = useState<FighterItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedFighter, setSelectedFighter] = useState<FighterItem | null>(
    null,
  );

  const ref = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleManualName = useCallback(
    (name: string) => {
      const nextName = name.trim();
      if (!nextName) return;
      setSelectedFighter(null);
      setQuery("");
      setOpen(false);
      onChange(nextName, nextName, true);
    },
    [onChange],
  );

  const loadFighters = useCallback(async (searchName?: string) => {
    if (searchName && searchName.trim()) {
      setLoading(true);
      try {
        const result = await getFighters({ name: searchName, pageSize: 100 });
        setFighters(Array.isArray(result.data) ? result.data : []);
      } catch {
        setFighters([]);
      } finally {
        setLoading(false);
      }
      return;
    }

    if (_fightersCache.data) {
      setFighters(_fightersCache.data);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      if (!_fightersCache.promise) prefetchFighters();
      const data = await _fightersCache.promise!;
      setFighters(data);
    } catch {
      setFighters([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleOpen = () => {
    if (!open) {
      setOpen(true);
      loadFighters(query || undefined);
    }
  };

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      loadFighters(query || undefined);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, open, loadFighters]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) {
        if (allowManualEntry && query.trim()) {
          handleManualName(query);
          return;
        }
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [allowManualEntry, handleManualName, query]);

  useEffect(() => {
    if (!currentId) {
      setSelectedFighter(null);
      return;
    }
    if (fighters.length > 0) {
      const found = fighters.find(
        (f) => f.id === currentId || f.id === Number(currentId),
      );
      if (found) {
        setSelectedFighter(found);
        return;
      }
    }
    if (currentName) {
      const [firstName, ...rest] = currentName.split(" ");
      setSelectedFighter({
        id: Number(currentId),
        firstName,
        lastName: rest.join(" ") || undefined,
      });
    }
  }, [currentId, currentName, fighters]);

  const handleSelect = (fighter: FighterItem) => {
    const name = [fighter.firstName, fighter.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();
    setSelectedFighter(fighter);
    setQuery("");
    setOpen(false);
    onChange(fighter.id, name);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedFighter(null);
    setQuery("");
    onChange(0, "");
  };

  const displayName = selectedFighter
    ? [selectedFighter.firstName, selectedFighter.lastName]
        .filter(Boolean)
        .join(" ")
        .trim()
    : currentName || "";

  const borderClass = open
    ? "border-blue-500 ring-2 ring-blue-100"
    : isInvalid
      ? "border-red-400 bg-red-50"
      : "border-slate-200 hover:border-slate-400";
  const queryText = query.trim();
  const canUseManualName =
    allowManualEntry &&
    !!queryText &&
    !fighters.some((fighter) => {
      const fighterName = [fighter.firstName, fighter.lastName]
        .filter(Boolean)
        .join(" ")
        .trim()
        .toLowerCase();
      return fighterName === queryText.toLowerCase();
    });

  return (
    <div ref={ref} className="relative w-full">
      <div
        onClick={handleOpen}
        className={`flex items-center gap-2 h-10 w-full rounded-lg border bg-white px-3 cursor-pointer transition-all duration-150 ${borderClass}`}
      >
        {!open && selectedFighter && (
          <FighterAvatar fighter={selectedFighter} size="sm" />
        )}

        {open ? (
          <input
            autoFocus
            type="text"
            value={query}
            placeholder={tr("searchFighterByName")}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (allowManualEntry && e.key === "Enter" && query.trim()) {
                e.preventDefault();
                handleManualName(query);
              }
            }}
            onClick={(e) => e.stopPropagation()}
            className="flex-1 text-[14px] text-slate-800 bg-transparent outline-none placeholder:text-slate-400 min-w-0"
          />
        ) : (
          <span
            className={`flex-1 text-[14px] truncate ${
              displayName ? "text-slate-800" : "text-slate-400"
            }`}
          >
            {displayName || placeholder}
          </span>
        )}

        {loading && open && (
          <svg
            className="w-4 h-4 text-blue-400 animate-spin flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z"
            />
          </svg>
        )}

        {!open && displayName && (
          <button
            type="button"
            onMouseDown={handleClear}
            className="text-slate-300 hover:text-slate-500 transition flex-shrink-0"
          >
            <svg
              className="w-3.5 h-3.5"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.5}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18 18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}

        {!loading && (
          <svg
            className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform duration-150 ${open ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        )}
      </div>

      {open && (
        <ul className="absolute z-50 mt-1 w-full max-h-60 overflow-y-auto bg-white border border-slate-200 rounded-xl shadow-lg py-1">
          {loading ? (
            <li className="px-4 py-3 text-[13px] text-slate-400 text-center">
              {tr("loadingFighters")}
            </li>
          ) : fighters.length === 0 && !canUseManualName ? (
            <li className="px-4 py-3 text-[13px] text-slate-400 text-center">
              {tr("noFightersFoundDropdown")}
            </li>
          ) : (
            <>
            {canUseManualName && (
              <li
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleManualName(query);
                }}
                className="flex items-center gap-3 px-3 py-2.5 cursor-pointer rounded-xl transition-colors hover:bg-[#EBEBEC]"
              >
                <div className="w-9 h-9 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center flex-shrink-0 text-blue-600 font-semibold">
                  +
                </div>
                <span className="text-[13px] font-semibold text-slate-700 truncate">
                  {tr("useManualValue", { value: queryText })}
                </span>
              </li>
            )}
            {fighters.map((fighter) => {
              const name =
                [fighter.firstName, fighter.lastName]
                  .filter(Boolean)
                  .join(" ")
                  .trim() || tr("fighterNumber", { id: fighter.id });
              const isSelected =
                selectedFighter?.id === fighter.id ||
                Number(currentId) === fighter.id;

              return (
                <li
                  key={fighter.id}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelect(fighter);
                  }}
                  className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer rounded-xl transition-colors hover:bg-[#EBEBEC] ${
                    isSelected ? "bg-[#EBEBEC]" : ""
                  }`}
                >
                  <FighterAvatar fighter={fighter} size="md" />

                  <div className="flex flex-col min-w-0">
                    <span
                      className={`text-[13px] font-semibold truncate ${
                        isSelected ? "text-blue-700" : "text-slate-700"
                      }`}
                    >
                      {name}
                    </span>
                    {fighter.weightClass && (
                      <span className="text-[11px] text-slate-400 truncate">
                        {humanizeLabel(fighter.weightClass)}
                      </span>
                    )}
                  </div>

                  {isSelected && (
                    <svg
                      className="w-4 h-4 text-blue-500 flex-shrink-0 ml-auto"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2.5}
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m4.5 12.75 6 6 9-13.5"
                      />
                    </svg>
                  )}
                </li>
              );
            })}
            </>
          )}
        </ul>
      )}
    </div>
  );
};
